import { VenueDetailsType } from '@api';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import z from 'zod';

const TRANS_KEY = 'VENUE_FORM.ERRORS';

const buildVenueFormSchema = (t: TFunction<'venues'>) =>
  z.object({
    name: z.string().min(1, { message: t(`${TRANS_KEY}.NAME_REQUIRED`) }),
    shortName: z.string(),
    venuePromoterId: z.array(z.string()),
    category: z
      .string()
      .min(1, { message: t(`${TRANS_KEY}.CATEGORY_REQUIRED`) }),
    website: z.string(),
    address: z.object({
      street: z.string().min(1, { message: t(`${TRANS_KEY}.STREET_REQUIRED`) }),
      addition: z.string(),
      zip: z.string().min(1, { message: t(`${TRANS_KEY}.ZIP_REQUIRED`) }),
      city: z.string().min(1, { message: t(`${TRANS_KEY}.CITY_REQUIRED`) }),
      cityEnglish: z.string(),
      state: z.string(),
      country: z
        .string()
        .min(1, { message: t(`${TRANS_KEY}.COUNTRY_REQUIRED`) }),
    }),
  });

type VenueFormSchemaType = ReturnType<typeof buildVenueFormSchema>;
export type VenueFormValuesType = z.infer<VenueFormSchemaType>;

export type UseVenueFormSchemaReturnType = {
  schema: VenueFormSchemaType;
  defaultValues: VenueFormValuesType;
};

export const useVenueFormSchema = (
  initialValues?: VenueDetailsType
): UseVenueFormSchemaReturnType => {
  const { t } = useTranslation('venues');
  const schema = buildVenueFormSchema(t);

  const defaultValues: VenueFormValuesType = {
    name: initialValues?.name ?? '',
    shortName: initialValues?.shortName ?? '',
    venuePromoterId: initialValues?.venuePromoterId ?? [],
    category: initialValues?.category ?? 'CLUB',
    website: initialValues?.website ?? '',
    address: {
      street: initialValues?.address.street ?? '',
      addition: initialValues?.address.addition ?? '',
      zip: initialValues?.address.zip ?? '',
      city: initialValues?.address.city ?? '',
      country: initialValues?.address.country ?? '',
      cityEnglish: initialValues?.address.cityEnglish ?? '',
      state: initialValues?.address.state ?? '',
    },
  };

  return {
    schema,
    defaultValues,
  };
};
